import { LDFlags } from '../../../../constants/flags';
import { useFlag } from '../../../../hooks';
import { useMarketplaceExternalId } from '../../../../services/marketplace/externalId';
import { useGetLeadByExternalId } from '../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { useGetLeadByExternalIdConditionReportSchema } from '../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalIdConditionReportSchema';
import { useGetLeadByExternalIdPhotos } from '../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalIdPhotos';
import { decodeAnswers, stepsCompleteCount } from './conditionReport/encodeDecodeAnswers';
import { PHOTOS_REQUIRED } from './constants';
import useDevLeadOverrides from './hooks/useDevLeadOverrides';
import { isLeadOnMarketplacePath, resolveListingStatus } from './listingStatus';
import { buildPhotoSlotMap } from './photos/buildPhotoSlotMap';
import {
  AskingPriceState,
  ChecklistItemStatus,
  ConditionReportState,
  MarketplaceState,
  PhotosState,
} from './types';

const deriveStatus = (count: number, total: number): ChecklistItemStatus => {
  if (total > 0 && count >= total) return 'complete';
  if (count > 0) return 'inProgress';
  return 'notStarted';
};

const MARKETPLACE_POLL_INTERVAL_MS = 15_000;

const useMarketplaceDashboardState = (dealId: string) => {
  const externalId = useMarketplaceExternalId(dealId);

  // No visibility guard needed: React Query pauses interval refetches while the tab is hidden (refetchIntervalInBackground defaults false)
  const leadQuery = useGetLeadByExternalId(
    { externalId },
    {
      query: {
        refetchInterval: (query) =>
          isLeadOnMarketplacePath(query.state.data) ? MARKETPLACE_POLL_INTERVAL_MS : false,
      },
    },
  );
  const photosQuery = useGetLeadByExternalIdPhotos({ externalId });
  const reportQuery = useGetLeadByExternalIdConditionReportSchema({ externalId });

  const photos = photosQuery.data;
  const report = reportQuery.data;

  const lead = useDevLeadOverrides(leadQuery.data);

  const activeDriverAsk = lead?.offers?.find(
    (o) => o.type === 'driver_ask' && o.status === 'pending',
  );
  const sellerConfidenceEnabled = useFlag(LDFlags.MARKETPLACE_SELLER_CONFIDENCE);
  const almostLiveEnabled = useFlag(LDFlags.MARKETPLACE_ALMOST_LIVE);
  const almostLiveViewsEnabled = sellerConfidenceEnabled && almostLiveEnabled;
  const listingStatus = resolveListingStatus(lead, almostLiveViewsEnabled);

  const askingPrice: AskingPriceState = activeDriverAsk
    ? { status: 'complete', price: activeDriverAsk.amount }
    : { status: 'notStarted', price: null };

  const reportSteps = report?.steps ?? [];
  const sectionsComplete =
    reportSteps.length > 0 ? stepsCompleteCount(reportSteps, decodeAnswers(reportSteps)) : 0;
  const sectionsTotal = reportSteps.length;
  const conditionReport: ConditionReportState = {
    status: deriveStatus(sectionsComplete, sectionsTotal),
    sectionsComplete,
    sectionsTotal,
  };

  const photosUploaded = buildPhotoSlotMap(photos ?? []).size;
  const photosState: PhotosState = {
    status: deriveStatus(photosUploaded, PHOTOS_REQUIRED),
    photosUploaded,
    photosTotal: PHOTOS_REQUIRED,
  };

  const marketplaceState: MarketplaceState = {
    listingStatus,
    conditionReport,
    photos: photosState,
    askingPrice,
  };

  const isAskingPriceLocked =
    conditionReport.status !== 'complete' || photosState.status !== 'complete';

  const isLoading = leadQuery.isLoading || photosQuery.isLoading || reportQuery.isLoading;

  const itemsComplete = isLoading
    ? null
    : Number(conditionReport.status === 'complete') +
      Number(photosState.status === 'complete') +
      Number(askingPrice.status === 'complete');

  return {
    externalId,
    lead,
    photos: photos ?? [],
    marketplaceState,
    isAskingPriceLocked,
    isLoading,
    itemsComplete,
    error: leadQuery.error ?? photosQuery.error ?? reportQuery.error,
    refetch: () => Promise.all([leadQuery.refetch(), photosQuery.refetch(), reportQuery.refetch()]),
  };
};

export default useMarketplaceDashboardState;
